import type { UseMutationResult } from '@tanstack/react-query';
import type { GooglePlaceDto, Location } from '@up2stay/up2promo-api-client';
import { Button, cn, Input, Label } from '@up2stay/up2promo-ui';
import { Settings2 } from 'lucide-react';

import { LocationAddressEditor } from '@/core/components/locations/location-address-editor';
import type { LocationAddressDetails } from '@/core/components/locations/location-details-form';
import { ShellLink } from '@/core/components/navigation/shell-link';
import { DangerZone } from '@/core/components/settings/danger-zone';
import {
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';
import { SettingsSection } from '@/core/components/settings/settings-section';
import { AutosaveStatusCue } from '@/core/components/ui/autosave-status-cue';
import type { AutosaveCueStatus } from '@/core/hooks/use-autosave-cue';
import { useIsMobile } from '@/core/hooks/use-mobile';
import { shellPaths } from '@/core/router/app-paths';

import type { LocationDetailDict } from './location-detail-types';

export type LocationAutosaveCue = AutosaveCueStatus;

/** Hard cap — prevents runaway titles breaking header / list cards. */
export const LOCATION_NAME_MAX_LENGTH = 120;

export function LocationDetailInformationTab({
  location,
  editNameValue,
  onEditNameChange,
  onEditNameBlur,
  contactEmailValue,
  onContactEmailChange,
  contactEmailError,
  contactPhoneValue,
  onContactPhoneChange,
  contactPhoneError,
  onContactBlur,
  addressDetails,
  onAddressDetailsChange,
  onAddressBlur,
  latitude,
  longitude,
  onCoordinatesChange,
  onPlaceSelect,
  deleteMutation,
  autosaveStatus = 'idle',
  canManage = true,
  dict,
}: {
  location: Location;
  editNameValue: string;
  onEditNameChange: (value: string) => void;
  onEditNameBlur?: () => void;
  contactEmailValue: string;
  onContactEmailChange: (value: string) => void;
  /** Inline validation message; the value is not saved while set. */
  contactEmailError?: string | null;
  contactPhoneValue: string;
  onContactPhoneChange: (value: string) => void;
  contactPhoneError?: string | null;
  onContactBlur?: () => void;
  addressDetails: LocationAddressDetails | null;
  onAddressDetailsChange: (details: LocationAddressDetails) => void;
  onAddressBlur?: () => void;
  latitude: number | undefined;
  longitude: number | undefined;
  onCoordinatesChange: (
    latitude: number | undefined,
    longitude: number | undefined,
  ) => void;
  onPlaceSelect?: (place: GooglePlaceDto) => void;
  deleteMutation: UseMutationResult<void, Error, void, unknown>;
  autosaveStatus?: AutosaveCueStatus;
  /** `locations:manage` — every location mutation needs it server-side. */
  canManage?: boolean;
  dict?: LocationDetailDict;
}) {
  const d = dict;
  const isMobile = useIsMobile();
  const reputationSetupHref = `${shellPaths.reputationSetup}?locationId=${location.id}&tab=responses`;
  const identityTitle = d?.identity?.title ?? d?.editName?.label ?? 'Identity';
  const addressTitle = d?.address?.title ?? 'Address';
  const reputationTitle = d?.reputationSettings?.title ?? 'Reputation settings';
  const identityFooter =
    d?.identity?.description ?? d?.contact?.description ?? undefined;
  const addressFooter = d?.address?.description ?? undefined;
  const reputationInfo =
    d?.reputationSettings?.description ??
    'Review analysis, AI replies, and Google connections.';

  return (
    <div className="flex min-w-0 w-full flex-col gap-6">
      <AutosaveStatusCue
        status={autosaveStatus}
        labels={{
          idle: d?.autosave?.idle,
          saving: d?.autosave?.saving,
          saved: d?.autosave?.saved,
          error: d?.autosave?.error,
        }}
      />

      <SettingsSection
        id="location-identity"
        title={identityTitle}
        footer={identityFooter}
        grouped
      >
        <SettingsGroupRows>
          <SettingsGroupRow>
            <div className="flex flex-col gap-2">
              <Label htmlFor="location-name">{d?.editName?.label}</Label>
              <Input
                id="location-name"
                value={editNameValue}
                maxLength={LOCATION_NAME_MAX_LENGTH}
                onChange={(e) => onEditNameChange(e.target.value)}
                onBlur={onEditNameBlur}
                disabled={!canManage}
                className={isMobile ? 'w-full' : 'max-w-md'}
                aria-label={d?.editName?.label}
              />
            </div>
          </SettingsGroupRow>
          <SettingsGroupRow separated>
            <div className="flex flex-col gap-2">
              <Label htmlFor="location-contact-email">
                {d?.contact?.emailLabel ?? 'Email'}
              </Label>
              <Input
                id="location-contact-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                value={contactEmailValue}
                onChange={(e) => onContactEmailChange(e.target.value)}
                onBlur={onContactBlur}
                disabled={!canManage}
                placeholder={
                  d?.contact?.emailPlaceholder ?? 'e.g. info@example.com'
                }
                className={cn(
                  isMobile ? 'w-full' : 'max-w-md',
                  contactEmailError && 'border-destructive',
                )}
                aria-label={d?.contact?.emailLabel ?? 'Contact email'}
                aria-invalid={contactEmailError ? true : undefined}
                aria-describedby={
                  contactEmailError ? 'location-contact-email-error' : undefined
                }
              />
              {contactEmailError ? (
                <p
                  id="location-contact-email-error"
                  className="text-[13.5px] leading-relaxed text-destructive"
                >
                  {contactEmailError}
                </p>
              ) : null}
            </div>
          </SettingsGroupRow>
          <SettingsGroupRow separated>
            <div className="flex flex-col gap-2">
              <Label htmlFor="location-contact-phone">
                {d?.contact?.phoneLabel ?? 'Phone'}
              </Label>
              <Input
                id="location-contact-phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={contactPhoneValue}
                onChange={(e) => onContactPhoneChange(e.target.value)}
                onBlur={onContactBlur}
                disabled={!canManage}
                placeholder={
                  d?.contact?.phonePlaceholder ?? 'e.g. +49 30 123456'
                }
                className={cn(
                  isMobile ? 'w-full' : 'max-w-md',
                  contactPhoneError && 'border-destructive',
                )}
                aria-label={d?.contact?.phoneLabel ?? 'Contact phone'}
                aria-invalid={contactPhoneError ? true : undefined}
                aria-describedby={
                  contactPhoneError ? 'location-contact-phone-error' : undefined
                }
              />
              {contactPhoneError ? (
                <p
                  id="location-contact-phone-error"
                  className="text-[13.5px] leading-relaxed text-destructive"
                >
                  {contactPhoneError}
                </p>
              ) : null}
            </div>
          </SettingsGroupRow>
        </SettingsGroupRows>
      </SettingsSection>

      <SettingsSection
        id="location-address"
        title={addressTitle}
        footer={addressFooter}
        grouped
      >
        <SettingsGroupRow>
          <div
            className="min-w-0"
            onBlur={(event) => {
              const next = event.relatedTarget;
              if (next instanceof Node && event.currentTarget.contains(next)) {
                return;
              }
              onAddressBlur?.();
            }}
          >
            <LocationAddressEditor
              addressDetails={addressDetails}
              onAddressDetailsChange={onAddressDetailsChange}
              latitude={latitude}
              longitude={longitude}
              onCoordinatesChange={onCoordinatesChange}
              onPlaceSelect={onPlaceSelect}
              disabled={!canManage}
              name={editNameValue || location.name}
              labels={{
                searchLabel: d?.address?.searchLabel,
                searchPlaceholder: d?.address?.searchPlaceholder,
                searchHint: d?.address?.searchHint,
                addressLabel: d?.address?.addressLabel,
                selectedEmptyLabel: d?.address?.selectedEmptyLabel,
                addressEditHint: d?.address?.addressEditHint,
                mapPreviewLabel: d?.address?.mapPreviewLabel,
                noCoordinatesLabel: d?.address?.noCoordinatesLabel,
                mapUnavailableLabel: d?.address?.mapUnavailableLabel,
                dragHintLabel: d?.address?.dragHintLabel,
              }}
              compactMap
            />
          </div>
        </SettingsGroupRow>
      </SettingsSection>

      <SettingsSection
        id="location-reputation-settings"
        title={reputationTitle}
        info={reputationInfo}
        grouped
      >
        <SettingsGroupRow>
          <div
            className={
              isMobile
                ? 'flex w-full flex-col'
                : 'flex items-center justify-between gap-3'
            }
          >
            <Button
              variant="outline"
              asChild
              className={isMobile ? 'w-full' : undefined}
            >
              <ShellLink to={reputationSetupHref}>
                <Settings2 className="mr-2 h-4 w-4" />
                {d?.reputationSettings?.button ?? 'Open Reputation setup'}
              </ShellLink>
            </Button>
          </div>
        </SettingsGroupRow>
      </SettingsSection>

      {location.name && canManage ? (
        <DangerZone
          title={d?.delete?.title ?? 'Delete location'}
          description={
            d?.delete?.description ?? 'This action cannot be undone.'
          }
          deleteTitle={d?.delete?.title ?? 'Delete location'}
          deleteDescription={
            d?.delete?.description ?? 'This action cannot be undone.'
          }
          deleteButtonLabel={d?.delete?.button ?? 'Delete location'}
          expandLabel={d?.delete?.expand ?? 'Show destructive actions'}
          collapseLabel={d?.delete?.collapse ?? 'Hide destructive actions'}
          confirmName={location.name}
          onDelete={async () => {
            await deleteMutation.mutateAsync();
          }}
          dict={{
            dialogTitle: d?.delete?.dialogTitle ?? 'Delete location',
            dialogDescription:
              d?.delete?.dialogDescription ??
              'Enter the location name to confirm deletion.',
            confirmLabel: d?.delete?.confirmLabel ?? 'Location name',
            confirmPlaceholder: d?.delete?.confirmPlaceholder ?? location.name,
            cancel: d?.delete?.cancel ?? 'Cancel',
            confirm: d?.delete?.confirm ?? 'Delete',
            nameMismatch: d?.delete?.nameMismatch ?? 'Name does not match',
            success: d?.delete?.success ?? 'Location deleted',
            error: d?.delete?.error ?? 'Could not delete location',
          }}
        />
      ) : null}
    </div>
  );
}
